import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PhotoIcon from "@mui/icons-material/Photo";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import ReactDOM from "react-dom";

const TREE_IMAGE_WIDTH = 200;

const TreeNodeLabel = ({
  node,
  dayPercentage,
  totalAccumulatedPoints,
  siblingMaxAccumulatedPoints,
  editingNode,
  editingTitle,
  editingDailyPoints,
  editingUnit,
  newNodeId,
  inputRef,
  showImageButtons,
  showImages,
  showNodeButtons,
  imageGeneratingForNodes,
  highlightedNodes,
  showPoints, // Added showPoints prop
  setEditingNode,
  setEditingTitle,
  setEditingDailyPoints,
  setEditingUnit,
  handleTitleChange,
  handleTitleSubmit,
  handleKeyPress,
  handleFocus,
  regenerateImageForTitle,
  saveCustomImageToGCS,
  updateNodePoints,
  handleDecrementOpen,
  handleDailyPointsChange,
  handleDailyPointsSubmit,
  handleUnitChange,
  handleUnitSubmit,
  addNewChild,
  moveNodeUp,
  moveNodeDown,
  handleDeleteOpen,
  isUrgent,
  onToggleUrgent,
}) => {
  const theme = useTheme();

  const estimateLines = (text, charsPerLine = 28) => {
    if (!text) return 1;
    const lines = text.split("\n").length;
    const approx = Math.ceil(text.length / charsPerLine);
    return Math.max(lines, approx, 1);
  };

  const hasActions = showPoints || showNodeButtons;
  const imageColumnWidth = showImages
    ? TREE_IMAGE_WIDTH + 40
    : showImageButtons
    ? 40
    : null;

  const gridTemplateColumns = [
    "minmax(160px, 1fr)",
    hasActions ? "auto" : null,
    imageColumnWidth !== null ? `${imageColumnWidth}px` : null,
  ]
    .filter(Boolean)
    .join(" ");

  // Allow the title to naturally take up the available column width. Using 100%
  // here lets the grid column determine final size based on which controls are
  // visible.
  const titleElementWidth = "100%";

  return (
    <Box
      id={`node-label-${node.id}`}
      sx={{
        display: "grid",
        gridTemplateColumns,
        gridTemplateRows: "auto auto auto",
        gap: 1,
        alignItems: "center",
        backgroundColor:
          highlightedNodes[node.id] === "gain"
            ? "rgba(0,255,0,0.15)"
            : highlightedNodes[node.id] === "lose"
            ? "rgba(255,0,0,0.15)"
            : highlightedNodes[node.id] === "focus"
            ? "rgba(0,0,255,0.15)"
            : "inherit",
      }}
    >
      {/* Column 1: Title */}
      <Box gridColumn={1} gridRow="1 / span 4" sx={{ width: "100%" }}>
        {(editingNode?.id === node.id || newNodeId === node.id) &&
        editingTitle ? (
          <Tooltip title="Edit the activity title">
            <TextField
              value={editingNode?.title || ""}
              onChange={(e) => handleTitleChange(e, node)}
              data-testid="node-title-edit-input" // Added data-testid
              onBlur={() => handleTitleSubmit(node)}
              onKeyDown={(e) => handleKeyPress(e, node, "title")}
              inputRef={inputRef}
              onFocus={handleFocus}
              autoFocus
              multiline
              maxRows={10}
              minRows={estimateLines(editingNode?.title || "", 28)}
              sx={{
                minWidth: "160px",
                width: titleElementWidth,
                "& .MuiInputBase-input": {
                  // Target the actual input/textarea element
                  // Keep existing font styles
                  fontSize: theme.typography.h6.fontSize,
                  fontFamily: theme.typography.h6.fontFamily,
                  fontWeight: theme.typography.h6.fontWeight,
                  lineHeight: theme.typography.h6.lineHeight,
                  // boxSizing: 'border-box', // Consider if padding issues arise
                },
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to edit the activity title">
            <Typography
              variant="h6"
              data-testid="node-title" // Added data-testid
              onClick={(e) => {
                e.stopPropagation();
                setEditingNode(node);
                setEditingTitle(true);
              }}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: "70px",
                width: titleElementWidth,
              }}
            >
              {node.title}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Column 2: Points, progress, and actions */}
      {node.title !== "Life" && (
        <>
          {/* Top line */}
          {showNodeButtons && (
            <Box
              gridColumn={2}
              gridRow={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {/* Actions like move, add, delete are disabled if setEditingNode is a basic stub, implying read-only context */}
              <Tooltip title="Move activity up">
                <IconButton
                  size="small"
                  onClick={(e) => moveNodeUp(e, node)}
                  sx={{ color: "gray" }}
                  data-testid="node-move-up-button" // Added data-testid
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move activity down">
                <IconButton
                  size="small"
                  onClick={(e) => moveNodeDown(e, node)}
                  sx={{ color: "gray" }}
                  data-testid="node-move-down-button" // Added data-testid
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isUrgent ? "Unmark as Urgent" : "Mark as Urgent"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleUrgent(); // Use the passed prop
                  }}
                  sx={{ color: isUrgent ? theme.palette.warning.main : "gray" }}
                  data-testid="node-toggle-urgent-button" // Added data-testid
                >
                  <ReportProblemIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add a sub-activity">
                <IconButton
                  size="small"
                  onClick={(e) => addNewChild(e, node)}
                  sx={{ color: "orange" }}
                  data-testid="node-add-child-button" // Added data-testid
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete this activity">
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteOpen(e, node)}
                  sx={{ color: "gray" }}
                  data-testid="node-delete-button" // Added data-testid
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {/* Middle line */}
          {showPoints && (
            <Box
              gridColumn={2}
              gridRow={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {/* Point update buttons are disabled if setEditingNode is a basic stub */}
              <Tooltip title="Mark accomplished and gain a point">
                <IconButton
                  size="small"
                  onClick={(e) => updateNodePoints(e, node, 1)}
                  data-testid="node-increment-points-button" // Added data-testid
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                  sx={{
                    color:
                      highlightedNodes[node.id] === "gain"
                        ? "gold"
                        : highlightedNodes[node.id] === "focus"
                        ? "dodgerblue"
                        : "green",
                    ...(highlightedNodes[node.id] === "gain" && {
                      animation: "glow 10s ease-in-out infinite alternate",
                    }),
                    "@keyframes glow": {
                      from: { filter: "drop-shadow(0 0 5px gold)" },
                      to: { filter: "drop-shadow(0 0 15px gold)" },
                    },
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Box sx={{ width: "25px" }}>
                <Tooltip title="Total points earned for this activity">
                  <Typography
                    variant="body2"
                    sx={{ mr: "10px", whiteSpace: "nowrap" }}
                  >
                    {node.points}
                  </Typography>
                </Tooltip>
              </Box>
              <Tooltip title="Lose a point for this activity">
                <IconButton
                  size="small"
                  onClick={(e) => handleDecrementOpen(e, node)}
                  data-testid="node-decrement-points-button" // Added data-testid
                  sx={{
                    color:
                      highlightedNodes[node.id] === "lose" ? "gold" : "red",
                    ...(highlightedNodes[node.id] === "lose" && {
                      animation: "glow 10s ease-in-out infinite alternate",
                    }),
                    "@keyframes glow": {
                      from: { filter: "drop-shadow(0 0 5px gold)" },
                      to: { filter: "drop-shadow(0 0 15px gold)" },
                    },
                    mr: "10px",
                  }}
                  disabled={
                    typeof setEditingNode === "function" &&
                    setEditingNode.toString().replace(/\s/g, "") === "()=>{}"
                  }
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Points earned today / planned for today">
                <Box sx={{ mr: "19px", width: "55px" }}>
                  {node.today ? node.today : 0} /{" "}
                  {(editingNode?.id === node.id || newNodeId === node.id) &&
                  editingDailyPoints ? (
                    <TextField
                      size="small"
                      value={editingNode?.dailyPoints || 10}
                      onChange={(e) => handleDailyPointsChange(e, node)}
                      onBlur={() => handleDailyPointsSubmit(node)}
                      onKeyDown={(e) => handleKeyPress(e, node, "dailyPoints")}
                      inputRef={inputRef}
                      onFocus={handleFocus}
                      autoFocus
                      sx={{ minWidth: 40 }}
                      data-testid="node-daily-points-edit-input" // Added data-testid
                    />
                  ) : (
                    <span
                      variant="body1"
                      data-testid="node-daily-points-display" // Added data-testid
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingNode(node);
                        setEditingDailyPoints(true);
                      }}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 40,
                      }}
                    >
                      {node.dailyPoints}
                    </span>
                  )}
                </Box>
              </Tooltip>
            </Box>
          )}
          {showPoints && (
            <Box
              gridRow={3} // This remains gridRow 3 for the "Unit"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Tooltip title="Unit of measurement for this activity">
                <Box sx={{ width: "190px" }}>
                  {(editingNode?.id === node.id || newNodeId === node.id) &&
                  editingUnit ? (
                    <TextField
                      size="small"
                      value={editingNode?.unit}
                      onChange={(e) => handleUnitChange(e, node)}
                      onBlur={() => handleUnitSubmit(node)}
                      onKeyDown={(e) => handleKeyPress(e, node, "unit")}
                      inputRef={inputRef}
                      onFocus={handleFocus}
                      autoFocus
                      sx={{ minWidth: "190px" }}
                      data-testid="node-unit-edit-input" // Added data-testid
                    />
                  ) : (
                    <span
                      variant="body1"
                      data-testid="node-unit-display" // Added data-testid
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingNode(node);
                        setEditingUnit(true);
                      }}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: "190px",
                      }}
                    >
                      {node.unit}
                    </span>
                  )}
                </Box>
              </Tooltip>
            </Box>
          )}
          {/* Bottom line */}
          {showPoints && (
            <Box
              gridColumn={2}
              gridRow={4}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Box sx={{ width: "190px", mb: "10px" }}>
                <Tooltip
                  title={`Represents this activity's total accumulated points (${totalAccumulatedPoints}) relative to the maximum among its direct siblings (${siblingMaxAccumulatedPoints})`}
                >
                  <LinearProgress
                    variant="determinate"
                    value={(() => {
                      let progressValue = 0;
                      if (siblingMaxAccumulatedPoints > 0) {
                        progressValue =
                          (totalAccumulatedPoints /
                            siblingMaxAccumulatedPoints) *
                          100;
                      } else if (totalAccumulatedPoints > 0) {
                        progressValue = 100;
                      }
                      return Math.max(0, Math.min(100, progressValue));
                    })()}
                    sx={{ width: "100%", height: "8px", borderRadius: "4px" }}
                  />
                </Tooltip>
                {/* <Tooltip title="Today's progress toward the daily goal">
                <LinearProgress
                  variant="determinate"
                  color="success"
                  value={dayPercentage}
                />
              </Tooltip> */}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Column 3: Image and actions */}
      {(showImages && node.image) || showImageButtons ? (
        <Box
          gridColumn={hasActions ? 3 : 2}
          gridRow="1 / span 4"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            width: imageColumnWidth,
            justifyContent: "center",
          }}
        >
          {showImages && node.image && (
            <Tooltip title="Click to view the image full screen">
              <Box
                sx={{
                  position: "relative",
                  width: TREE_IMAGE_WIDTH,
                  height: "auto",
                  cursor: "pointer",
                  "data-testid": "node-image-box", // Added for the clickable box
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const dialogContainer = document.createElement("div");
                  document.body.appendChild(dialogContainer);
                  const FullScreenImageDialog = () => {
                    const [open, setOpen] = React.useState(true);
                    const handleClose = () => {
                      setOpen(false);
                      setTimeout(() => {
                        ReactDOM.unmountComponentAtNode(dialogContainer);
                        document.body.removeChild(dialogContainer);
                      }, 100);
                    };
                    return (
                      <Dialog fullScreen open={open} onClose={handleClose}>
                        <IconButton
                          onClick={handleClose}
                          sx={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            color: "white",
                            zIndex: 1000,
                          }}
                        >
                          <CloseIcon fontSize="large" />
                        </IconButton>
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "black",
                          }}
                        >
                          <Box
                            component="img"
                            src={node.image}
                            alt={`${node.title} full screen`}
                            sx={{ maxWidth: "100%", maxHeight: "100%" }}
                          />
                        </Box>
                      </Dialog>
                    );
                  };
                  ReactDOM.render(<FullScreenImageDialog />, dialogContainer);
                }}
              >
                <Box
                  component="img"
                  src={node.image}
                  alt={`${node.title} image`}
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    borderRadius: 1,
                  }}
                  data-testid="node-image" // Added data-testid for the image itself
                />
                <Box
                  sx={{
                    pointerEvents: "none",
                    position: "absolute",
                    inset: 0,
                    borderRadius: 1,
                    boxShadow: `inset 0 0 0 2px ${theme.palette.background.paper}, inset 0 0 8px 2px ${theme.palette.background.paper}`,
                  }}
                />
              </Box>
            </Tooltip>
          )}
          {showImageButtons && (
            <Box sx={{ ml: 1, display: "flex", flexDirection: "column" }}>
              {imageGeneratingForNodes.includes(node.title) ? (
                <Tooltip title="Generating new image">
                  <IconButton size="small" disabled sx={{ color: "orange" }}>
                    <CircularProgress size={20} color="success" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Generate a new AI image">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      regenerateImageForTitle(node.title);
                    }}
                    sx={{ color: "orange" }}
                    data-testid="node-generate-ai-image-button" // Added data-testid
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Upload or link a custom image">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    saveCustomImageToGCS(node.title);
                  }}
                  sx={{ color: "gray" }}
                  data-testid="node-upload-custom-image-button" // Added data-testid
                >
                  <PhotoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default TreeNodeLabel;
